import type { ApiClient } from '@center-technologies/shared-api-clients';

/**
 * Who the signed-in person chose to act as in the identity picker: their own identity (a
 * session with no organization) or one of their center_users, in that user's organization.
 * Remembered server-side, in the identity's preferences
 * (`GET`/`PUT /identities/:identityId/identity-selection`, graph-service), so the choice
 * follows the person across browsers and devices.
 */
export type IdentitySelection =
  | { kind: 'self' }
  | { kind: 'org'; organizationId: string; userId: string };

/** A remembered selection resolved against the loaded identity: who to act as, and where. */
export interface RestoredSelection {
  userId: string;
  organizationId: string | null;
}

/** The identity/users shape `GET /identities/:id/users` returns, as far as this module reads it. */
export interface SelectableIdentity {
  identity: { id: string };
  users: ReadonlyArray<{ id: string; organization?: unknown }>;
}

const selectionPath = (identityId: string) => `/identities/${encodeURIComponent(identityId)}/identity-selection`;

/**
 * The organization a center_user belongs to. The graph returns `organization` as an array of
 * organization vertices (the picker has always read `[0]`); a single object is accepted too.
 */
export function organizationIdOfUser(user: { organization?: unknown }): string | undefined {
  const organization = Array.isArray(user.organization) ? user.organization[0] : user.organization;
  const id = (organization as { id?: unknown } | undefined)?.id;
  return typeof id === 'string' && id ? id : undefined;
}

/** `value` as a selection, or null when it is not a well-formed one. */
export function parseIdentitySelection(value: unknown): IdentitySelection | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as { kind?: unknown; organizationId?: unknown; userId?: unknown };
  if (candidate.kind === 'self') return { kind: 'self' };
  if (
    candidate.kind === 'org' &&
    typeof candidate.organizationId === 'string' &&
    candidate.organizationId &&
    typeof candidate.userId === 'string' &&
    candidate.userId
  ) {
    return { kind: 'org', organizationId: candidate.organizationId, userId: candidate.userId };
  }
  return null;
}

/**
 * Remembers the person's explicit picker choice on their identity. Only the caller's own
 * identity can be written (the route is self-only); an organization choice must name the
 * identity's own user in that organization, or the server refuses it. Rejects on failure.
 */
export async function saveIdentitySelection(
  graphClient: ApiClient,
  identityId: string,
  selection: IdentitySelection
): Promise<void> {
  await graphClient.put(selectionPath(identityId), selection);
}

/**
 * The choice remembered for this identity, or null when there is none. Rejects when it could
 * not be read; the caller decides whether that blocks sign-in (it should not).
 */
export async function readIdentitySelection(graphClient: ApiClient, identityId: string): Promise<IdentitySelection | null> {
  const body = await graphClient.get<{ selection?: unknown }>(selectionPath(identityId));
  return parseIdentitySelection(body?.selection);
}

/** Whether a remembered choice is "act as myself". */
export function isOwnIdentitySelection(selection: IdentitySelection | null): boolean {
  return selection?.kind === 'self';
}

/**
 * The organization a remembered choice may restore, or null. Only an organization the
 * session's own membership list (from `/auth/bootstrap`) still contains is ever returned: a
 * choice remembered before the person was removed from an organization must not select it again.
 */
export function restorableOrganizationId(
  selection: IdentitySelection | null,
  memberOrganizationIds: readonly string[]
): string | null {
  if (selection?.kind !== 'org') return null;
  return memberOrganizationIds.includes(selection.organizationId) ? selection.organizationId : null;
}

/**
 * The organization to enter when there is no remembered choice to restore: bootstrap's
 * `defaultOrganizationId` (the organization of the identity's default user) while it is one of
 * the session's member organizations, else the tenant the session already carries, else the
 * first membership. Null only when the session belongs to no organization.
 */
export function defaultTargetOrganizationId(
  defaultOrganizationId: string | null | undefined,
  activeTenantId: string | null | undefined,
  memberOrganizationIds: readonly string[]
): string | null {
  if (defaultOrganizationId && memberOrganizationIds.includes(defaultOrganizationId)) return defaultOrganizationId;
  return activeTenantId || memberOrganizationIds[0] || null;
}

/**
 * The identity's center_user in `organizationId` — `preferredUserId` (its default user) when that
 * user belongs there, else its first user there. Null when it has none in that organization.
 * Whoever the app acts as must be a user of the organization the session's tenant names.
 */
export function userInOrganization(
  identity: SelectableIdentity,
  organizationId: string,
  preferredUserId?: string | null
): string | null {
  const inOrganization = identity.users.filter((user) => organizationIdOfUser(user) === organizationId);
  const preferred = inOrganization.find((user) => user.id === preferredUserId);
  return (preferred ?? inOrganization[0])?.id ?? null;
}

/**
 * Who to act as for a remembered choice, if it still names something this identity can act
 * as: the identity itself, or one of its center_users that still belongs to the remembered
 * organization, which must still be one of the session's member organizations. Else null.
 */
export function resolveRestorableSelection(
  selection: IdentitySelection | null,
  identity: SelectableIdentity,
  memberOrganizationIds: readonly string[]
): RestoredSelection | null {
  if (!selection) return null;
  if (selection.kind === 'self') return { userId: identity.identity.id, organizationId: null };

  const organizationId = restorableOrganizationId(selection, memberOrganizationIds);
  if (!organizationId) return null;
  const user = identity.users.find((candidate) => candidate.id === selection.userId);
  if (!user || organizationIdOfUser(user) !== organizationId) return null;
  return { userId: user.id, organizationId };
}

/**
 * The organizations the signed-in identity belongs to right now, read from `GET /auth/organizations` (a read; `/auth/bootstrap` can provision)
 * (graph membership, the same list the server validates `x-center-organization-id` against).
 * Rejects when the service does not answer.
 */
export async function fetchMemberOrganizationIds(organizationClient: ApiClient): Promise<string[]> {
  const response = await organizationClient.get<{ organizationIds?: string[] }>('/auth/organizations');
  const body = ((response as { data?: unknown } | undefined)?.data ?? response) as { organizationIds?: string[] } | undefined;
  return body?.organizationIds ?? [];
}
